import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

/**
 * Rail horizontal à accroche.
 *
 * Sur mobile c'est un simple défilement au doigt (scroll-snap natif, aucun JS
 * de glissement à maintenir). Sur pointeur fin, deux flèches apparaissent au
 * survol et se désactivent aux extrémités.
 *
 * @param {React.ReactNode} title       titre du rail
 * @param {React.ReactNode} [icon]      icône devant le titre
 * @param {string}          [subtitle]
 * @param {React.ReactNode} [action]    lien « tout voir » à droite
 * @param {string}          [itemClass] largeur des items (`rail-item`, `rail-item-wide`…)
 */
export default function Rail({
  title,
  icon,
  subtitle,
  action,
  itemClass = 'rail-item',
  children,
  className = '',
}) {
  const trackRef = useRef(null);
  const [edges, setEdges] = useState({ start: true, end: false });

  const readEdges = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;
    const max = track.scrollWidth - track.clientWidth;
    setEdges({
      start: track.scrollLeft <= 2,
      // 2 px de tolérance : les navigateurs n'atteignent pas toujours le max exact
      end: track.scrollLeft >= max - 2,
    });
  }, []);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return undefined;

    readEdges();
    track.addEventListener('scroll', readEdges, { passive: true });

    // Les images arrivent après le premier rendu : la largeur change.
    const observer = new ResizeObserver(readEdges);
    observer.observe(track);

    return () => {
      track.removeEventListener('scroll', readEdges);
      observer.disconnect();
    };
  }, [readEdges, children]);

  const nudge = (direction) => {
    const track = trackRef.current;
    if (!track) return;
    // On avance d'un écran moins une carte, pour garder un repère visuel.
    const step = Math.max(track.clientWidth * 0.82, 240);
    track.scrollBy({ left: direction * step, behavior: 'smooth' });
  };

  const hasChildren = React.Children.count(children) > 0;
  if (!hasChildren) return null;

  return (
    <section className={`rail ${className}`}>
      {(title || action) && (
        <header className="rail-head">
          <div className="min-w-0">
            <h2 className="rail-title">
              {icon}
              <span className="truncate">{title}</span>
            </h2>
            {subtitle && <p className="rail-sub">{subtitle}</p>}
          </div>
          {action}
        </header>
      )}

      <button
        type="button"
        className="rail-arrow rail-arrow-prev"
        onClick={() => nudge(-1)}
        disabled={edges.start}
        aria-label="Faire défiler vers la gauche"
      >
        <span>
          <ChevronLeft className="w-5 h-5" aria-hidden="true" />
        </span>
      </button>

      <div className="rail-track" ref={trackRef}>
        {React.Children.map(children, (child, index) =>
          child ? (
            <div className={itemClass} style={{ '--i': index }}>
              {child}
            </div>
          ) : null
        )}
      </div>

      <button
        type="button"
        className="rail-arrow rail-arrow-next"
        onClick={() => nudge(1)}
        disabled={edges.end}
        aria-label="Faire défiler vers la droite"
      >
        <span>
          <ChevronRight className="w-5 h-5" aria-hidden="true" />
        </span>
      </button>
    </section>
  );
}
